import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AuthenticatedLayout from '../Layouts/AuthenticatedLayout';
import CoffeeBlends from '../Components/CoffeeBlends';
import { useAuth } from "../context/AuthContext";
import {
  DEFAULT_ROUTE_CATEGORY_CONFIG,
  buildRouteCategoryConfig,
} from '../config/categoryRoutes';
import { useTopCategories } from '../hooks/useTopCategories';

const FALLBACK_ROUTE = '/coffees';

export default function CategoryPage() {
  const { user } = useAuth();
  const location = useLocation();
  const params = useParams();
  const slugParam = params.slug;
  const { topCategories } = useTopCategories();

  const routeCategoryConfig = useMemo(
    () => buildRouteCategoryConfig(topCategories, DEFAULT_ROUTE_CATEGORY_CONFIG),
    [topCategories]
  );

  const routeConfig = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
    const normalizedSlug = slugParam ? slugParam.toLowerCase() : '';
    const configFromPath = routeCategoryConfig[normalizedPath];
    if (configFromPath) {
      return configFromPath;
    }

    const matchingTopCategory =
      normalizedSlug &&
      topCategories.find(
        (category) =>
          category?.slug && category.slug.toLowerCase() === normalizedSlug
      );

    if (matchingTopCategory) {
      const title =
        matchingTopCategory.name ||
        normalizedSlug.charAt(0).toUpperCase() + normalizedSlug.slice(1);
      return {
        rootCategoryId: matchingTopCategory.id ? String(matchingTopCategory.id) : '',
        rootSlug: matchingTopCategory.slug || normalizedSlug,
        title,
        description:
          matchingTopCategory.description ||
          `Διαχειριστείτε την κατηγορία ${title}.`,
      };
    }

    if (normalizedSlug) {
      const friendlyTitle =
        normalizedSlug.charAt(0).toUpperCase() + normalizedSlug.slice(1);
      return {
        rootCategoryId: '',
        rootSlug: normalizedSlug,
        title: friendlyTitle,
        description: `Διαχειριστείτε την κατηγορία ${friendlyTitle}.`,
      };
    }

    return (
      routeCategoryConfig[FALLBACK_ROUTE] ||
      Object.values(routeCategoryConfig)[0] || {
        rootCategoryId: '',
        rootSlug: '',
        title: 'Κατηγορίες',
        description: 'Διαχειριστείτε τις κατηγορίες και τα προϊόντα τους.',
      }
    );
  }, [location.pathname, routeCategoryConfig, slugParam, topCategories]);

  useEffect(() => {
    document.title = `${routeConfig.title} - Coffee Roast City`;
  }, [routeConfig.title]);

  return (
    <AuthenticatedLayout user={user}>
      <CoffeeBlends
        rootCategoryId={routeConfig.rootCategoryId}
        rootSlug={routeConfig.rootSlug}
        pageTitleOverride={routeConfig.title}
        pageDescriptionOverride={routeConfig.description}
      />
    </AuthenticatedLayout>
  );
}
